import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import Breadcrumbs, {CrumbLink} from 'nav/Breadcrumbs';
import {LEGACY_FONT_PROXIMA_19} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BasicEvent} from 'util/Types';

import {TenantBreadcrumbsQuery} from './__generated/TenantBreadcrumbsQuery';

export const TENANT_BREADCRUMBS_QUERY: DocumentNode = gql`
  query TenantBreadcrumbsQuery @skipAudit @engComponent(name: TENANCY) {
    currentTenant {
      id
      displayName
      permissions {
        admin
      }
    }
  }
`;

export interface TenantBreadcrumbsProps {
  tenant: {displayName: string};
  onTenantClick: (e: BasicEvent) => void;
  organization?: {url_identifier: string; name: string};
  onOrganizationClick: (e: BasicEvent) => void;
}

function Divider(): JSX.Element {
  return <span className={css(styles.divider)}>&rsaquo;</span>;
}

export function TenantBreadcrumbs({
  tenant,
  onTenantClick,
  organization,
  onOrganizationClick,
}: TenantBreadcrumbsProps): JSX.Element {
  const crumbs = [<CrumbLink key="tenant" text={tenant.displayName} onClick={onTenantClick} />];
  if (organization != null) {
    crumbs.push(<CrumbLink key="organization" text={organization.name} onClick={onOrganizationClick} />);
  }
  return <Breadcrumbs crumbs={crumbs} divider={<Divider />} showTrailingDivider={organization == null} />;
}

export default function QueryingTenantBreadcrumbs(
  props: Omit<TenantBreadcrumbsProps, 'onTenantClick' | 'onOrganizationClick' | 'tenant'>
): JSX.Element | null {
  if (!BenchlingConfig.ENABLE_TENANT_ADMIN_CONSOLE) {
    return null;
  }

  function onTenantClick(e: BasicEvent): void {
    BenchlingUtil.navigate(e, '/admin');
  }
  function onOrganizationClick(_: BasicEvent): void {
    if (!props.organization) {
      return;
    }
    navigateToURL(`organizations/${props.organization.url_identifier}`);
  }

  const actions = {onTenantClick, onOrganizationClick};

  /* eslint-disable-next-line @typescript-eslint/explicit-function-return-type, react/no-unstable-nested-components -- Disabling for now, but worth fixing */
  const VerticalSpacer = () => (
    <div
      className="u-mini-spacing-bottom invisible"
      {...getTestProps({component: 'TenantBreadcrumbs', element: 'verticalSpacer'})}
    >
      <TenantBreadcrumbs tenant={{displayName: ''}} {...actions} {...props} />
    </div>
  );

  return (
    <BenchlingQuery<TenantBreadcrumbsQuery>
      query={TENANT_BREADCRUMBS_QUERY}
      // Render spacer while loading so component renders with consistent height
      renderLoading={() => <VerticalSpacer />}
    >
      {({currentTenant}) => {
        if (!currentTenant.permissions.admin) {
          return null;
        }
        return (
          <div className="u-mini-spacing-bottom u-gray-darker">
            <TenantBreadcrumbs tenant={currentTenant} {...actions} {...props} />
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  divider: {
    ...LEGACY_FONT_PROXIMA_19,
  },
});
